<template>
    <div
        v-if="!segment.isDeleted"
        ref="segment"
        :class="[
            'audio-editor-segment',
            {
                'audio-editor-segment--dragged': !!dragSide,
                [`audio-editor-segment--drag-${dragSide}`]: !!dragSide,
                'audio-editor-segment--selected': isActive,
                'audio-editor-segment--changed': hasChanges,
                'audio-editor-segment--cut': isCut
            }
        ]"
        :style="{
            width: `${width}px`,
            height: `${height}px`,
            left: `${left}px`
        }"
        @click.stop="handleClick"
    >
        <template v-if="canDurationAdjustment">
            <div class="audio-editor-segment__contros">
                <div class="audio-editor-segment__stick" @mousedown="startDrag" />
                <div class="audio-editor-segment__stick" @mousedown="startDrag" />
            </div>
            <div v-if="dragSide" class="audio-editor-segment__time">
                <span v-for="(time, i) in segmentTime" :key="i" v-text="`${time}s`" />
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'AudioEditorSegment',
    props: {
        height: {
            type: Number,
            required: true
        },
        segment: {
            type: Object,
            required: true
        },
        precision: {
            type: Number,
            required: true
        },
        offsetLeft: {
            type: Number,
            required: true
        },
        hasChanges: {
            type: Boolean,
            default: false
        },
        selectedSegment: {
            type: String,
            default: null
        }
    },

    inject: ['transition', 'transitionIs'],

    data() {
        return {
            initial: {},
            speed: this.segment.speed || 1,
            dragSide: undefined
        };
    },

    computed: {
        width() {
            if (this.hasChanges) {
                if (this.segment.type === 'trim') {
                    return 1;
                }

                // scale of current duration
                return this.segment.duration / this.precision;
            }

            return this.segment.duration / this.precision;
        },

        left() {
            if (this.hasChanges) {
                // scale of current duration
                return (this.segment.start + this.segment.shift) / this.precision;
            }

            return this.segment.start / this.precision;
        },
        segmentTime() {
            const { start = 0, end = 0 } = this.segment;
            return [start, end].filter(t => t > 0).map(t => (t / 1000).toFixed(0));
        },

        isActive() {
            return this.segment.id === this.selectedSegment;
        },

        isCut() {
            const { cut = false } = this.segment;
            return cut;
        },

        canDurationAdjustment() {
            const { isNew = false } = this.segment;
            return isNew;
        }
    },

    created() {
        this.initial = { ...this.segment };
    },

    methods: {
        startDrag({ target }) {
            this.dragSide = target.offsetLeft > 0 ? 'right' : 'left';
            document.addEventListener('mousemove', this.onDrag);
            document.addEventListener('mouseup', this.endDrag);
        },
        onDrag(event) {
            const { duration, start } = this.segment;
            const left = event.pageX - this.offsetLeft;
            const position = { duration, start };

            if (this.dragSide === 'left') {
                position.start = start - (this.left - left) * this.precision;
                position.duration = duration + (this.left - left) * this.precision;
            } else {
                position.duration = (this.width + left) * this.precision - start - duration;
            }

            this.updateDuration(event, position);
        },
        endDrag() {
            this.dragSide = undefined;
            document.removeEventListener('mousemove', this.onDrag);
            document.removeEventListener('mouseup', this.endDrag);
        },
        updateDuration(event, { start, duration }) {
            const { id } = this.segment;
            const params = { id, start, duration };
            this.transition({ type: 'DURATION_ADJUSTMENT', event, params });
        },
        handleClick(event) {
            if (this.isActive || this.transitionIs('completed')) return;
            const { id } = this.segment;
            const params = { id };
            this.transition({ type: 'SET_ACTIVE', event, params });
        }
    }
};
</script>

<style lang="scss" src="./AudioEditorSegment.scss"></style>
